import {
    IExamApplicationDetails,
    IExamApplicationList,
    IExamDetails,
    ExamApplicationList,
    IExamDayApplications,
} from '../../api/ApiClient';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import {
    GetListForDayData,
    IFetchExamAssignedApplicationsForDayProps, IFetchExamUnAssignedApplicationsProps,
} from 'components/models';

const initialState: IState = {
    examApplicationsList: null,
    examApplicationsListForAdmit: null,
    examUnAssignedApplicationsList: null,
    applicationDetails: null,
    examDetails: null,
    draftsList: null,
    examApplicationsForDayList: null,
    unratedExamApplicationsList: null,
    applicantDetailsForAdmit: null,
    examApplicationDetails: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExamUnAssignedApplicationsList(state, examUnAssignedApplicationsList: ExamApplicationList): IState {
            return { ...(state || {}), examUnAssignedApplicationsList };
        },
        clearExamUnAssignedApplicationsList(state): IState {
            return { ...(state || {}), examUnAssignedApplicationsList: null };
        },
        setUnratedExamApplicationsList(state: IState, unratedExamApplicationsList: IExamApplicationList): IState {
            return { ...state, unratedExamApplicationsList };
        },
        clearUnratedExamApplicationsList(state: IState): IState {
            return { ...state, unratedExamApplicationsList: null };
        },
        setExamApplicationsList(state: IState, examApplicationsList: IExamApplicationList): IState {
            return { ...state, examApplicationsList };
        },
        setExamApplicationsListForAdmit(state: IState, examApplicationsListForAdmit: IExamDayApplications): IState {
            return { ...state, examApplicationsListForAdmit };
        },
        clearExamApplicationsListForAdmit(state: IState): IState {
            return { ...state, examApplicationsListForAdmit: null };
        },
        setExamApplicationsForDay(state: IState, examApplicationsForDayList: IExamDayApplications): IState {
            return { ...state, examApplicationsForDayList };
        },
        clearExamApplicationsForDay(state: IState): IState {
            return { ...state, examApplicationsForDayList: null };
        },
        setUnassignedExamApplicationsForStage(state: IState, examUnAssignedApplicationsList: IExamApplicationList | null): IState {
            return { ...state, examUnAssignedApplicationsList };
        },
        clearUnassignedExamApplicationsForStage(state: IState): IState {
            return { ...state, examUnAssignedApplicationsList: null };
        },
        setExamApplicationDetails(state: IState, applicationDetails: IExamApplicationDetails): IState {
            return { ...state, applicationDetails };
        },
        setExamDetails(state, examDetails: IExamDetails): IState {
            return { ...state, examDetails };
        },
        setDrafts(state: IState, draftsList: ExamApplicationList | null): IState {
            return { ...state, draftsList };
        },
        clearExamApplications(state): IState {
            return { ...state, examApplicationsList: null };
        },
        clearApplicationDetails(state): IState {
            return { ...state, applicationDetails: null };
        },
        setDetails(state, examApplicationDetails: IState['examApplicationDetails']): IState {
            return { ...(state || {}), examApplicationDetails };
        },
        clearDetails(state): IState {
            return { ...(state || {}), examApplicationDetails: null };
        },
    },
    effects: (dispatch: any) => ({
        async fetchExamApplicationsAsync(props: IFetchExamApplicationsProps) {
            const examApplicationsList = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplications(
                props.examId,
                props.searchField,
                props.searchTerm,
                props.page,
                props.pageSize || 100,
                props.sortOrder,
                props.sortField,
            ));

            this.setExamApplicationsList(examApplicationsList);
        },
        async fetchExamApplicationsForDayAdmitAsync(data: IFetchExamAssignedApplicationsForDayProps) {
            const examApplicationsListForDayAdmit = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamDayApplications(
              data.examId,
              data.stage,
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setExamApplicationsListForAdmit(examApplicationsListForDayAdmit);
        },
        async fetchExamApplicationDetailsAsync(examApplicationId: string) {
            this.setExamApplicationDetails(null);
            const exam = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplication(examApplicationId));

            this.setExamApplicationDetails(exam);
        },
        async fetchExamApplicationsForDayAsync(data: IFetchExamAssignedApplicationsForDayProps) {
            const examApplicationsListForDay = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamDayApplications(
              data.examId,
              data.stage,
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setExamApplicationsForDay(examApplicationsListForDay);
        },
        async fetchUnratedExamApplications(data: GetListForDayData) {
            const unratedExamApplicationsList = await apiConnector.execApi(dispatch, apiClient => apiClient.getUnratedExamApplications(
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setUnratedExamApplicationsList(unratedExamApplicationsList);
        },
        async fetchExamDetailsAsync(id: string) {
            const examDetail = await apiConnector.execApi(dispatch, apiClient => apiClient.getExam(id));
            this.setExamDetails(examDetail);
        },
        async getExamApplicationDetailsAsync(examApplicationId: string) {
            this.setExamApplicationDetails(null);

            const examApplication = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplication(examApplicationId));
            this.setExamApplicationDetails(examApplication as IExamApplicationDetails);

            if (!examApplication) {
                this.setExamDetails(null);
            } else {
                const examDetail = await apiConnector.execApi(dispatch, apiClient => apiClient.getExam(examApplication.exam.id));
                this.setExamDetails(examDetail);
            }
        },
        async fetchExamUnAssignedApplicationsList(payload: IFetchExamUnAssignedApplicationsProps) {
            this.setExamUnAssignedApplicationsList(null);
        },
        async fetchExamApplicationDetails(examApplicationId: string) {
            const examApplicationDetails = await apiConnector.execApi(dispatch, api => api.getExamApplication(examApplicationId));

            this.setDetails(examApplicationDetails);

            return examApplicationDetails;
        },
    }),
});

export interface IFetchExamApplicationsProps {
    examId?: string;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

interface IState {
    examApplicationsList: IExamApplicationList | null;
    examApplicationsListForAdmit: IExamDayApplications | null;
    unratedExamApplicationsList: IExamApplicationList | null;
    examApplicationsForDayList: IExamDayApplications | null;
    examUnAssignedApplicationsList: IExamApplicationList | null;
    applicationDetails: IExamApplicationDetails | null;
    examDetails: IExamDetails | null;
    draftsList: ExamApplicationList | null;
    applicantDetailsForAdmit: IExamApplicationDetails | null;
    examApplicationDetails: IExamApplicationDetails | null;
}

export interface IApproveExamApplication {
    examApplicationId: string;
    signCode: string;
    isExpert?: boolean;
    captcha?: string;
}
